'use client';

import { gsap } from 'gsap';
import { useEffect, useRef } from 'react';
import GrillFeatureComparisonItem from './_GrillFeatureComparisonItem';
import Section from '@repo/ui/src/components/Section';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import type { HeaderVariationProps } from '@repo/ui/src/types';
import useProducts from '@hooks/product/useProducts';
import Product from '@models/product';

interface SeriesGrillFeatureComparisonClientBoundaryProps {
  headingLine1: string;
  headingLine2: string;
  headerVariation: HeaderVariationProps;
  buttonText: string;
  buttonLabel: string;
  products: Array<any>;
  _data: Array<any>;
}

const SeriesGrillFeatureComparisonClientBoundary = ({
  headingLine1,
  headingLine2,
  headerVariation,
  buttonText,
  buttonLabel,
  products: initialProducts,
  _data,
}: SeriesGrillFeatureComparisonClientBoundaryProps) => {
  const { products } = useProducts(
    _data.map((x) => {
      const match = initialProducts.find((p: any) => p.product.id === x._meta?.deliveryId);
      return new Product({ ...match, ...x });
    }),
  );

  const ref = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: '200px top',
        end: '40% top',
        scrub: 0.5,
      },
    });
    timeline.to(headingRef.current, { opacity: 0 });
  }, []);

  return (
    <section ref={ref}>
      <Section
        dark
        texture
        gutterX
        className="bg-neutral-600 relative before:absolute before:left-0 before:right-0 before:bottom-0 before:h-[50vh] before:bg-speckle before:bg-fixed"
      >
        <div ref={headingRef} className="sticky top-0 left-0 right-0 heading">
          <ToutContent
            dark
            flexible
            headingStyleVariant="h1"
            headingLight={headingLine1}
            heading={headingLine2}
            headerVariation={headerVariation}
            className="px-6 pt-16 pb-8 sm:pt-36"
          />
        </div>
        <div
          className="flex flex-col md:flex-row items-stretch py-6 lg:py-[3vw]"
        >
          {products.map((product, i) => (
            <GrillFeatureComparisonItem
              key={product?.sku}
              product={product}
              buttonText={buttonText}
              buttonLabel={buttonLabel}
              index={i}
            />
          ))}
        </div>
      </Section>
    </section>
  );
};

export default SeriesGrillFeatureComparisonClientBoundary;
